import { useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchMtrStructTree } from '#/api/imsMgmt/settings/mtrStruct/fetchTree';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { MTR_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import MtrSideMsg from '#/modules/imsMgmt/settings/mtr/components/MtrSideMsg';
import { buildMtrColumns } from '#/modules/imsMgmt/settings/mtr/data/mtrColumns';
import { getMtrApiErrorMessage } from '#/modules/imsMgmt/settings/mtr/helper/apiError';
import useMtrList from '#/modules/imsMgmt/settings/mtr/hooks/useMtrList';
import { fromTree } from '#/modules/imsMgmt/settings/mtrStruct/helper/structFilterModel';
import { mtrStructTreeQueryKey } from '#/utils/queryKeys';

const MtrListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.MTR_CREATE);
  const canUpdate = useCan(PERM.MTR_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useMtrList();

  const treeQuery = useQuery({
    queryKey: mtrStructTreeQueryKey(),
    queryFn: () => fetchMtrStructTree({ status: 'ACTIVE' }),
  });

  const structModel = useMemo(
    () => fromTree(treeQuery.data?.data ?? []),
    [treeQuery.data?.data],
  );

  const columns = useMemo(
    () => buildMtrColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      canShowEdit: () => canUpdate,
    }),
    [listQuery.filters, setFilter, canUpdate],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { structModel }),
    [columns, structModel],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.mtrCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">物料</span>
        </h1>
        <SideMsgLayout enableDockModes>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="ims.mtr.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getMtrApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋料號、名稱、備註或原廠料號"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={MTR_LIST_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>

          <MtrSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default MtrListPage;
